import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, SidePanel } from '@mysaiva/core'
import { EXPIRATION_OPTIONS, dateToExpiration } from '../../api-keys/scopeCatalog'
import type { ApiKey, ExpirationOption, UpdateApiKeyPayload } from '../../api-keys/apiKeys.types'
import { ScopePicker } from './ScopePicker'
import { FormErrorBanner } from './FormErrorBanner'
import styles from './ApiKeys.module.scss'

interface EditApiKeyPanelProps {
  open: boolean
  apiKey: ApiKey
  onClose: () => void
  onSubmit: (payload: UpdateApiKeyPayload) => void
  submitting?: boolean
  /** The last submit failed; the form stays editable so the user can retry. */
  error?: boolean
}

export function EditApiKeyPanel({
  open,
  apiKey,
  onClose,
  onSubmit,
  submitting,
  error
}: EditApiKeyPanelProps) {
  const { t } = useTranslation()
  const [expiration, setExpiration] = useState<ExpirationOption>(() =>
    dateToExpiration(apiKey.expiresAt)
  )
  // The preset is a lossy view of the real expiry date, so "same preset as
  // the one shown" does not mean "untouched": a key expiring in 3 days shows
  // as 30 days, and re-picking 30 days is a deliberate renewal. Track the
  // control's own edits instead of comparing values.
  const [expirationEdited, setExpirationEdited] = useState(false)
  const [scopes, setScopes] = useState<string[]>(apiKey.scopes)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setExpiration(dateToExpiration(apiKey.expiresAt))
    setExpirationEdited(false)
    setScopes(apiKey.scopes)
    setTouched(false)
  }, [open, apiKey])

  const scopesError =
    touched && scopes.length === 0 ? t('developerPlatform.apiKeys.form.scopesRequired') : undefined

  const submit = () => {
    setTouched(true)
    if (scopes.length === 0) return
    // The backend re-anchors any lifetime it receives, so the expiry is only
    // sent when the user explicitly picked an option in this session.
    const payload: UpdateApiKeyPayload = { scopes }
    if (expirationEdited) {
      payload.expiration = expiration
      payload.issuedAt = apiKey.issuedAt
    }
    onSubmit(payload)
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={t('developerPlatform.apiKeys.edit.title')}
      description={t('developerPlatform.apiKeys.edit.description')}
      actions={{
        cancel: { label: t('developerPlatform.apiKeys.actions.cancel'), onClick: onClose },
        primary: {
          label: t('developerPlatform.apiKeys.actions.save'),
          onClick: submit,
          loading: submitting,
          'data-testid': 'edit-key-submit'
        }
      }}
      className={styles.panelTokens}
      mask={false}
      data-testid="edit-key-panel"
    >
      <div className={styles.form}>
        {error && (
          <FormErrorBanner
            title={t('developerPlatform.apiKeys.edit.errorTitle')}
            text={t('developerPlatform.apiKeys.edit.errorText')}
            trigger={error}
            data-testid="edit-key-error"
          />
        )}
        <div className={styles.readonlyRows}>
          <div className={styles.readonlyRow}>
            <span>{t('developerPlatform.apiKeys.fields.name')}</span>
            <span data-testid="edit-key-name">{apiKey.name}</span>
          </div>
          <div className={styles.readonlyRow}>
            <span>{t('developerPlatform.apiKeys.fields.key')}</span>
            <span className={styles.mono}>{apiKey.maskedKey}</span>
          </div>
          {/* The environment is fixed at creation; the backend update contract has no such field. */}
          <div className={styles.readonlyRow}>
            <span>{t('developerPlatform.apiKeys.fields.environment')}</span>
            <span data-testid="edit-key-environment">
              {t(`developerPlatform.apiKeys.environment.${apiKey.environment}`)}
            </span>
          </div>
        </div>
        {/* Full width: the environment is read-only here, so Expiration has the row to itself. */}
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {t('developerPlatform.apiKeys.form.expiration')}
          </span>
          <Select
            value={expiration}
            onChange={value => {
              setExpiration(value as ExpirationOption)
              setExpirationEdited(true)
            }}
            options={EXPIRATION_OPTIONS.map(option => ({
              value: option,
              label: t(`developerPlatform.apiKeys.expiration.${option}`)
            }))}
            data-testid="edit-key-expiration"
          />
        </div>
        <div className={styles.divider} />
        <ScopePicker selected={scopes} onChange={setScopes} layout="inline" error={scopesError} />
      </div>
    </SidePanel>
  )
}
